/**
 * Main Application Component
 * 
 * Handles:
 * - Authentication state management
 * - Routing (authenticated vs unauthenticated)
 * - Bridge navigation (lore-mfe-kit / LORE_BRIDGE_NAVIGATION)
 * - Global error handling
 * 
 * @module App
 */

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ProfileProvider } from '@/contexts/ProfileContext';
import { NavBadgesProvider } from '@/contexts/NavBadgesContext';
import { MockLoginScreen } from '@/components/auth/MockLoginScreen';
import { TokenPasteLoginScreen } from '@/components/auth/TokenPasteLoginScreen';
import { AuthLandingScreen } from '@/components/auth/AuthLandingScreen';
import { AccountCreationScreen } from '@/components/auth/AccountCreationScreen';
import { SignInScreen } from '@/components/auth/SignInScreen';
import { ForgotPasswordScreen } from '@/components/auth/ForgotPasswordScreen';
import { ResetPasswordScreen } from '@/components/auth/ResetPasswordScreen';
import { BrowserAccessGuard } from '@/components/auth/BrowserAccessGuard';
import { MainLayout } from '@/components/layout/MainLayout';
import { RewardsRoute } from '@/components/mfe/RewardsRoute';
import { LoreBotRoute } from '@/components/mfe/LoreBotRoute';
import { CommunityRoute } from '@/components/mfe/CommunityRoute';
import { PrivacyRoute } from '@/components/mfe/PrivacyRoute';
import { NotificationsRoute } from '@/components/mfe/NotificationsRoute';
import { StreaksRoute } from '@/components/mfe/StreaksRoute';
import { MenuScreen } from '@/components/menu/MenuScreen';
import { HomeScreen } from '@/components/home/HomeScreen';
import { MoreScreen } from '@/components/more/MoreScreen';
import { LoreLoader } from '@sequelae/lore-core';
import '@sequelae/lore-core/dist/styles/base/variables.css';
import '@sequelae/lore-core/dist/styles/base/fonts.css';
import './App.css';

// Note: CommunicationBridge no longer requires initialization
// Token management and browser support are handled automatically

// Account creation is disabled by default per desktop spec (Section 8: Reduced Functionality)
const ENABLE_ACCOUNT_CREATION = process.env.REACT_APP_ENABLE_ACCOUNT_CREATION === 'true';

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Determine which login/creation screen to show based on auth mode
  const authMode = process.env.REACT_APP_AUTH_MODE || 'mock';

  // GCIP mode uses AuthLandingScreen as entry point, others use login screens
  const UnauthenticatedScreen = authMode === 'gcip'
    ? AuthLandingScreen
    : (authMode === 'token-paste' ? TokenPasteLoginScreen : MockLoginScreen);

  const unauthenticatedPath = authMode === 'gcip' ? '/auth' : '/login';

  // TEMPORARILY DISABLED: Clear hash fragments when on /home page
  // This was potentially causing refresh loops
  // TODO: Re-enable with better logic once refresh issue is resolved
  // useEffect(() => {
  //   if (location.pathname !== '/home') {
  //     return;
  //   }
  //   const clearHash = () => {
  //     const currentHash = window.location.hash;
  //     if (currentHash && currentHash !== '#') {
  //       console.log('[AppRoutes] Clearing hash:', currentHash);
  //       window.history.replaceState(null, '', window.location.pathname + window.location.search);
  //     }
  //   };
  //   const timeoutId = setTimeout(() => clearHash(), 100);
  //   const handleHashChange = () => {
  //     if (location.pathname === '/home') {
  //       setTimeout(clearHash, 50);
  //     }
  //   };
  //   window.addEventListener('hashchange', handleHashChange);
  //   return () => {
  //     clearTimeout(timeoutId);
  //     window.removeEventListener('hashchange', handleHashChange);
  //   };
  // }, [location.pathname]);

  // Redirect logic using declarative approach
  // Check if we need to redirect based on current auth state and location
  const authPaths = ['/login', '/auth', '/account-creation', '/sign-in', '/forgot-password', '/reset-password'];
  const isOnAuthPath = authPaths.includes(location.pathname);

  // Determine if redirect is needed
  const shouldRedirectToHome = !isLoading && isAuthenticated && isOnAuthPath;
  const shouldRedirectToAuth = !isLoading && !isAuthenticated && !isOnAuthPath;

  // Loading state
  if (isLoading) {
    return (
      <div className="app-loading">
        <LoreLoader size={100} />
      </div>
    );
  }

  // Perform redirects declaratively
  if (shouldRedirectToHome) {
    console.log('[AppRoutes] Redirecting to /home');
    return <Navigate to="/home" replace />;
  }

  if (shouldRedirectToAuth) {
    console.log('[AppRoutes] Redirecting to', unauthenticatedPath);
    return <Navigate to={unauthenticatedPath} replace />;
  }

  return (
    <Routes>
      {/* Unauthenticated routes */}
      <Route path="/login" element={<UnauthenticatedScreen />} />
      <Route path="/auth" element={<UnauthenticatedScreen />} />
      <Route path="/sign-in" element={<SignInScreen />} />
      {/* Account creation disabled by default per desktop spec - redirect to auth if disabled */}
      <Route
        path="/account-creation"
        element={ENABLE_ACCOUNT_CREATION ? <AccountCreationScreen /> : <Navigate to="/auth" replace />}
      />
      <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
      <Route path="/reset-password" element={<ResetPasswordScreen />} />

      {/* Authenticated routes */}
      <Route path="/" element={<MainLayout />}>
        <Route index element={<Navigate to="/home" replace />} />
        <Route path="home" element={<HomeScreen />} />
        <Route path="rewards" element={<RewardsRoute />} />
        <Route path="lorebot" element={<LoreBotRoute />} />
        <Route path="community" element={<CommunityRoute />} />
        <Route path="more" element={<MoreScreen />} />
        <Route path="privacy" element={<PrivacyRoute />} />
        <Route path="notifications" element={<NotificationsRoute />} />
        <Route path="streaks" element={<StreaksRoute />} />
        <Route path="account" element={<MenuScreen />} />
        <Route path="account/:subpage" element={<MenuScreen />} />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to={isAuthenticated ? "/home" : unauthenticatedPath} replace />} />
    </Routes>
  );
}

function App() {
  // Log environment on mount
  useEffect(() => {
    console.log('🚀 Lore Web Client initialized');
    console.log('Environment:', {
      NODE_ENV: process.env.NODE_ENV,
      REACT_APP_ENV: process.env.REACT_APP_ENV,
      REACT_APP_AUTH_MODE: process.env.REACT_APP_AUTH_MODE,
      REACT_APP_ENABLE_ACCOUNT_CREATION: ENABLE_ACCOUNT_CREATION,
    });
  }, []);

  // Listen for navigation messages from MFEs (postMessage from iframes or LORE_BRIDGE_NAVIGATION from web bridge polyfill)
  useEffect(() => {
    const getRoute = (targetTab?: string, path?: string, url?: string): string | null => {
      const tab = (targetTab ?? '').toString().toLowerCase();
      const p = (path ?? '').toString().toLowerCase();
      const u = (url ?? '').toString().toLowerCase();
      if (tab === 'rewards' || p.includes('rewards') || u.includes('rewards')) return '/rewards';
      if (tab === 'streaks' || p.includes('streaks') || u.includes('streaks')) return '/streaks';
      if (tab === 'lorebot' || p === '/lorebot' || u.includes('lorebot')) return '/lorebot';
      if (tab === 'community' || p.includes('community') || u.includes('community')) return '/community';
      if (tab === 'more' || p === '/more' || p.startsWith('/more/') || u === 'more') return '/more';
      if (tab === 'privacy' || p.includes('privacy') || u.includes('privacy')) return '/privacy';
      if (tab === 'account' || tab === 'menu') return '/account';
      if (tab === 'notifications' || p.includes('notifications') || u.includes('notifications')) return '/notifications';
      // Verification lives under Account (e.g. Rewards "Get Verified Now" → /account/verification)
      if (tab === 'verification' || p.includes('verification') || u.includes('verification')) return '/account/verification';
      if (tab === 'faq' || p.includes('faq') || u.includes('faq')) return '/faq';
      if (tab === 'help' || p.includes('help') || u.includes('help')) return '/help';
      return null;
    };

    const applyNavigation = (messageType: string, payload: Record<string, unknown> | undefined) => {
      const p = payload ?? {};
      if (['MFE_ROUTE_EXTERNAL', 'MFE_PUSH_NAVIGATE', 'UNIFIED_NAVIGATION'].includes(messageType)) {
        const route = getRoute(
          p.targetTab as string | undefined,
          p.path as string | undefined,
          p.url as string | undefined
        );
        if (route) {
          console.log('[App] Navigating to:', route);
          window.location.href = route;
        }
      } else if (messageType === 'BROWSER_OPEN' && p.url) {
        window.open(String(p.url), '_blank');
      }
    };

    const handleMFENavigation = (event: MessageEvent) => {
      if (event.data?.type !== 'MFE_NAVIGATION') return;
      const { payload, messageType } = event.data;
      console.log('[App] Received MFE navigation message:', { messageType, payload });
      applyNavigation(messageType, payload);
    };

    const handleBridgeNavigation = (event: CustomEvent<{ messageType: string; payload: Record<string, unknown> }>) => {
      const { messageType, payload } = event.detail;
      console.log('[App] Received bridge navigation (LoreBot coins/streaks etc.):', { messageType, payload });
      applyNavigation(messageType, payload);
    };

    window.addEventListener('message', handleMFENavigation);
    window.addEventListener('LORE_BRIDGE_NAVIGATION', handleBridgeNavigation as EventListener);
    return () => {
      window.removeEventListener('message', handleMFENavigation);
      window.removeEventListener('LORE_BRIDGE_NAVIGATION', handleBridgeNavigation as EventListener);
    };
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <ProfileProvider>
          <NavBadgesProvider>
            <BrowserAccessGuard>
              <AppRoutes />
            </BrowserAccessGuard>
          </NavBadgesProvider>
        </ProfileProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
